import {
  getWorkspaceId,
  useAppSelector,
  useLazyGetDocumentsQuery,
  useLazyGetMediaFromRedingtonQuery,
} from '@zonofi/seller-rtk-store';
import { isDocumentRequest, isRedingtonMedia, isS3Media } from '../functions';
import {
  GenerateLinkParams,
  PresignedUrlPayload,
  ImageFileExtensions,
  AudioFileExtensions,
} from '@zonofi/common';
import { useGetPresignedUrl } from './get-presigned-url';
import { useCallback } from 'react';

export type MediaAction = 'open' | 'download' | 'render';

function isValidUrl(value: string): boolean {
  if (!value) return false;

  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function isS3Url(url: URL): boolean {
  return url.hostname.includes('amazonaws.com') && url.hostname.includes('s3');
}

function isPresignedS3Url(url: string): boolean {
  try {
    const u = new URL(url);
    // Must be S3
    const isS3Host = isS3Url(u);
    if (!isS3Host) return false;

    const params = u.searchParams;
    // Core AWS signing indicators
    return (
      params.has('X-Amz-Signature') ||
      params.has('X-Amz-Credential') ||
      params.has('X-Amz-Algorithm') ||
      params.has('X-Amz-Security-Token') // temporary creds
    );
  } catch {
    return false;
  }
}

/**
 * Infers MIME type from filename or extension.
 */
function getContentTypeFromFilename(filename: string): string {
  if (!filename || !filename.length) return 'application/octet-stream';

  const ext = filename.split('.').pop()?.toLowerCase();
  if (!ext) return 'application/octet-stream';

  // 🖼️ Image Files
  if (ImageFileExtensions.includes(ext)) {
    if (['whatsapp-image', 'sendPaymentRequestQR'].includes(ext)) return ext;
    if (ext === 'svg') return 'image/svg+xml';
    return 'image/' + (ext === 'jpg' ? 'jpeg' : ext);
  }

  // 🔊 Audio Files
  if (AudioFileExtensions.includes(ext)) {
    if (ext === 'mpga' || ext === 'mpeg') return 'audio/mpeg';
    return 'audio/' + ext;
  }

  // 📄 Document or Data Files
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'csv':
      return 'text/csv';
    default:
      return 'application/octet-stream';
  }
}

function getUrlParts(url: string): string[] {
  if (!isPresignedS3Url(url)) return [];

  const parsed = new URL(url);
  const parts = parsed.pathname.split('/').filter(Boolean);
  return parts;
}

function extractMessageIdFromS3Url(keyParts: string[]) {
  try {
    if (keyParts[0] !== 'channels' || keyParts.length < 4) return undefined;

    return {
      channelId: keyParts[1],
      messageId: keyParts[2],
      filename: keyParts[3],
    };
  } catch {
    return undefined;
  }
}

function extractDocumentParamsFromS3Url(keyParts: string[]) {
  try {
    if (keyParts[0] !== 'workspaces' || keyParts.length < 3) return undefined;

    const workspaceId = keyParts[1];
    const documentType = keyParts[2];
    const rest = keyParts.slice(3);

    const entityId = rest.join('/');

    return {
      workspaceId,
      documentType,
      entityId,
    };
  } catch {
    return undefined;
  }
}

function extractParamsFromLink(link: string): GenerateLinkParams | undefined {
  const urlParts = getUrlParts(link);
  const s3 = extractMessageIdFromS3Url(urlParts);
  if (s3) {
    return {
      messageId: s3.messageId,
      name: s3.filename,
      cntType: getContentTypeFromFilename(s3.filename),
    };
  }

  const doc = extractDocumentParamsFromS3Url(urlParts);
  if (doc) {
    return {
      documentType: doc.documentType,
      entityId: doc.entityId,
    };
  }

  return undefined;
}

interface HandleMediaDownloadPayload {
  onSuccess: (url: string, action: MediaAction) => void;
  onError: (error?: any) => void;
  filename?: string;
  link?: string;
}

export const useHandleMediaDownload = ({
  onSuccess,
  onError,
  filename,
  link,
}: HandleMediaDownloadPayload) => {
  const workspaceId = useAppSelector(getWorkspaceId);
  const [fetchDoc, docResponse] = useLazyGetDocumentsQuery();
  const [fetchMedia, mediaResponse] = useLazyGetMediaFromRedingtonQuery();
  const { getPresignedUrl } = useGetPresignedUrl();

  const getMedia = useCallback(
    async (
      generateLinkParams?: GenerateLinkParams,
      action?: MediaAction,
      skipLink: boolean = false
    ) => {
      try {
        let url: string | undefined;

        // ----------------------
        // ✅ STEP 1: LINK FIRST
        // ----------------------
        if (!skipLink && link && isValidUrl(link)) {
          // 👉 Public → return immediately
          if (!isPresignedS3Url(link)) {
            onSuccess(link, action ?? 'open');
            return;
          }

          // 👉 Presigned → extract params
          const extracted = extractParamsFromLink(link);
          if (extracted) {
            generateLinkParams = extracted;
          }
        }

        // 1️⃣ Case: Document Request
        if (generateLinkParams && isDocumentRequest(generateLinkParams)) {
          const response = await fetchDoc({
            workspaceId,
            ...generateLinkParams,
          }).unwrap();

          url = response?.[0]?.url;
        }
        // 2️⃣ Case: Redington Media
        else if (generateLinkParams && isRedingtonMedia(generateLinkParams)) {
          const response = await fetchMedia({
            workspaceId,
            ...generateLinkParams,
          }).unwrap();

          url = response?.url;
        }
        // 3️⃣️⃣ Case: S3 Media
        else if (generateLinkParams && isS3Media(generateLinkParams)) {
          const fileNameToUse = filename || generateLinkParams.name;

          const presignedParams: PresignedUrlPayload = {
            method: 'get',
            ...generateLinkParams,
            name: fileNameToUse,
            cntType:
              generateLinkParams?.cntType ||
              getContentTypeFromFilename(fileNameToUse),
          };

          url = await getPresignedUrl(presignedParams);
        }

        // ----------------------
        // ✅ FALLBACK
        // ----------------------

        if (!url && link && isValidUrl(link)) {
          url = link;
        }

        if (!url) {
          console.error('No valid media source found');
          return;
        }

        onSuccess(url, action ?? 'open');
      } catch (error) {
        console.error('[Download Media] Error: ', error);
        if (action !== 'render') onError(error);
      }
    },
    [
      workspaceId,
      fetchDoc,
      fetchMedia,
      getPresignedUrl,
      onSuccess,
      onError,
      link,
      filename,
    ]
  );

  const isLoading =
    mediaResponse.isFetching ||
    mediaResponse.isLoading ||
    docResponse.isLoading;

  return { getMedia, isLoading };
};
